import { useState, useEffect, useCallback } from "react";
import { getProvinces, getWardsByProvince, ProvinceItem, WardItem } from "../api/LocationInfo";

interface LocationOption {
    label: string;
    value: string;
}

// In-memory singleton so multiple components share one request
let provincesPromise: Promise<ProvinceItem[]> | null = null;
let provincesCache: ProvinceItem[] | null = null;

function loadProvincesOnce(): Promise<ProvinceItem[]> {
    if (provincesCache) return Promise.resolve(provincesCache);
    if (!provincesPromise) {
        provincesPromise = getProvinces().then((items) => {
            provincesCache = items;
            return items;
        }).catch((err) => {
            provincesPromise = null; // allow retry on failure
            throw err;
        });
    }
    return provincesPromise;
}

export function useLocationData(initialProvinceCode?: string | null) {
    const [provinceOptions, setProvinceOptions] = useState<LocationOption[]>([]);
    const [wardOptions, setWardOptions] = useState<LocationOption[]>([]);
    const [loadingProvinces, setLoadingProvinces] = useState(false);
    const [loadingWards, setLoadingWards] = useState(false);

    // Load provinces once
    useEffect(() => {
        let cancelled = false;
        setLoadingProvinces(true);
        loadProvincesOnce()
            .then((items) => {
                if (cancelled) return;
                setProvinceOptions(items.map((p) => ({ label: p.full_name || p.name, value: p.code })));
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoadingProvinces(false); });
        return () => { cancelled = true; };
    }, []);

    // Load wards by province_code
    const loadWards = useCallback(async (provinceCode: string) => {
        setLoadingWards(true);
        setWardOptions([]);
        try {
            const items = await getWardsByProvince(provinceCode);
            setWardOptions(items.map((w) => ({ label: w.full_name || (w.prefix ? `${w.prefix} ${w.name}` : w.name), value: w.code || String(w.id) })));
        } catch {
            setWardOptions([]);
        } finally {
            setLoadingWards(false);
        }
    }, []);

    // Auto-load wards if initial province_code is provided
    useEffect(() => {
        if (initialProvinceCode) {
            loadWards(initialProvinceCode);
        }
    }, [initialProvinceCode, loadWards]);

    return {
        provinceOptions,
        wardOptions,
        loadingProvinces,
        loadingWards,
        loadWards,
        setWardOptions,
    };
}
